import { Tabs } from '@base-ui/react/tabs'
import { twMerge } from 'tailwind-merge'
import type { PartProps } from '../../../lib/part-props'

/**
 * The segmented control has two sizes, not Button's six: its type is fixed at 13px, so a taller
 * strip only adds air around the same words. `--tabs-h` is the one number; the inset (h/10, snapped
 * to a half pixel so the thumb's edges stay crisp) and the corner radius (h/2) are ratios of it.
 */
const SIZES = {
  sm: '[--tabs-h:24px] [--tabs-pad:8px]',
  md: '[--tabs-h:28px] [--tabs-pad:10px]',
}

type TabsListSize = keyof typeof SIZES

interface TabsListProps extends PartProps<Tabs.List.Props> {
  className?: string
  indicatorClassName?: string
  size?: TabsListSize
  /** Icon tabs: no padding, and the list sizes to its tabs instead of filling its container. Give each Tab an aria-label. */
  iconOnly?: boolean
}

function TabsList({ className, indicatorClassName, size = 'md', iconOnly, children, ...props }: TabsListProps) {
  return (
    <Tabs.List
      className={twMerge(
        SIZES[size],
        '[--tabs-inset:round(calc(var(--tabs-h)/10),0.5px)] [--tabs-radius:calc(var(--tabs-h)/2)]',
        'bg-element corner-squircle relative isolate grid h-[var(--tabs-h)] w-full select-none auto-cols-[minmax(max-content,1fr)] grid-flow-col rounded-[var(--tabs-radius)] p-[var(--tabs-inset)]',
        // A segment stays at least as wide as the list is tall. Shrunk to the glyph, the thumb is the
        // size of a checkbox and there's no target around the icon.
        iconOnly && 'inline-grid w-fit auto-cols-[minmax(var(--tabs-h),1fr)] [--tabs-pad:0px]',
        className,
      )}
      {...props}
    >
      <Tabs.Indicator
        className={twMerge(
          // Raised is white on light but darker than the track on dark, so dark takes a stronger element fill.
          'bg-background-raised dark:bg-element-active corner-squircle shadow-xs absolute bottom-[var(--tabs-inset)] left-0 top-[var(--tabs-inset)] z-0 w-[var(--active-tab-width)] rounded-[calc(var(--tabs-radius)-var(--tabs-inset))] [translate:var(--active-tab-left)_0]',
          'transition-[translate] duration-150 ease-out motion-reduce:transition-none',
          indicatorClassName,
        )}
      />
      {children}
    </Tabs.List>
  )
}

export { TabsList }
export type { TabsListSize }
